import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { FaPlus, FaTimes } from 'react-icons/fa';
import TenantFeatureTenantSelector from './TenantFeatureTenantSelector';
import {
  TENANT_FEATURE_BULK_OPERATIONS,
  buildBulkTenantFeaturesPayload,
  buildTenantFeaturesPayload,
  deriveSelectedTenants,
  tenantFeaturesPayloadHasChanges,
  getTenantDisplayName,
  normalizeTenantFeatures,
  summarizeBulkTenantFeatureTargets,
  tenantFeaturesHaveChanges,
} from '../tenantHelpers';

const OPERATION_LABELS = {
  [TENANT_FEATURE_BULK_OPERATIONS.ADD]: 'Add features',
  [TENANT_FEATURE_BULK_OPERATIONS.REMOVE]: 'Remove features',
  [TENANT_FEATURE_BULK_OPERATIONS.REPLACE]: 'Replace all',
};

const SEGMENT_LABELS = {
  [TENANT_FEATURE_BULK_OPERATIONS.ADD]: 'Add',
  [TENANT_FEATURE_BULK_OPERATIONS.REMOVE]: 'Remove',
  [TENANT_FEATURE_BULK_OPERATIONS.REPLACE]: 'Replace All',
};

const formatTargetSummaryLine = (targetSummary) => (
  Object.entries(targetSummary || {})
    .map(([type, count]) => `${type} ${count}`)
    .join(' · ')
);

const formatLabelPreview = (labels) => {
  const list = labels || [];
  if (!list.length) return 'No labels';
  if (list.length <= 2) return list.join(' · ');
  return `${list.slice(0, 2).join(' · ')} +${list.length - 2}`;
};

const TenantFeaturesPanel = ({
  tenants,
  labelOptions,
  featureSuggestions,
  selectedTenantIds,
  onSelectedTenantIdsChange,
  onApply,
  isSaving,
  error,
}) => {
  const [operation, setOperation] = useState(TENANT_FEATURE_BULK_OPERATIONS.ADD);
  const [inputFeatures, setInputFeatures] = useState([]);
  const [newFeature, setNewFeature] = useState('');
  const [showReview, setShowReview] = useState(false);
  const [singleEditedFeatures, setSingleEditedFeatures] = useState([]);
  const [singleOriginalFeatures, setSingleOriginalFeatures] = useState([]);

  const selectedTenants = useMemo(
    () => deriveSelectedTenants(tenants, selectedTenantIds),
    [tenants, selectedTenantIds]
  );

  const isSingleTenant = selectedTenants.length === 1;
  const singleTenant = isSingleTenant ? selectedTenants[0] : null;

  useEffect(() => {
    if (!singleTenant) {
      setSingleEditedFeatures([]);
      setSingleOriginalFeatures([]);
      return;
    }
    const initial = normalizeTenantFeatures(singleTenant.tenantFeatures || []);
    setSingleEditedFeatures(initial);
    setSingleOriginalFeatures(initial);
  }, [singleTenant?.id, singleTenant?.tenantFeatures]);

  useEffect(() => {
    setShowReview(false);
    setInputFeatures([]);
    setNewFeature('');
  }, [selectedTenants.length, operation]);

  const addInputFeature = (value = newFeature) => {
    const feature = String(value || '').trim();
    if (!feature) return;
    if (inputFeatures.some((item) => item.toLowerCase() === feature.toLowerCase())) return;
    setInputFeatures((prev) => [...prev, feature]);
    setNewFeature('');
  };

  const removeInputFeature = (feature) => {
    setInputFeatures((prev) => prev.filter((item) => item !== feature));
  };

  const addSingleFeature = (value = newFeature) => {
    const feature = String(value || '').trim();
    if (!feature) return;
    if (singleEditedFeatures.some((item) => item.toLowerCase() === feature.toLowerCase())) return;
    setSingleEditedFeatures((prev) => [...prev, feature]);
    setNewFeature('');
  };

  const removeSingleFeature = (feature) => {
    setSingleEditedFeatures((prev) => prev.filter((item) => item !== feature));
  };

  const plannedPayload = useMemo(() => {
    if (!selectedTenants.length || isSingleTenant) return {};
    return buildBulkTenantFeaturesPayload(selectedTenants, operation, inputFeatures);
  }, [selectedTenants, isSingleTenant, operation, inputFeatures]);

  const targetSummary = useMemo(
    () => summarizeBulkTenantFeatureTargets(selectedTenants),
    [selectedTenants]
  );

  const singleHasChanges = isSingleTenant
    && tenantFeaturesHaveChanges(singleOriginalFeatures, singleEditedFeatures);

  const bulkCanProceed = selectedTenants.length > 1 && inputFeatures.length > 0;

  const handleOpenReview = async () => {
    if (isSingleTenant) {
      if (!singleHasChanges) return;
      setShowReview(true);
      return;
    }

    if (!bulkCanProceed) return;

    if (operation === TENANT_FEATURE_BULK_OPERATIONS.REPLACE) {
      const confirm = await Swal.fire({
        title: 'Replace all tenant features?',
        html: `
          <p>This will <strong>replace</strong> existing Tenant Features on ${selectedTenants.length} selected tenant(s).</p>
          <p>Any features not included in your new list will be removed.</p>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Continue to review',
        cancelButtonText: 'Cancel',
      });
      if (!confirm.isConfirmed) return;
    }

    setShowReview(true);
  };

  const handleApply = async () => {
    if (!selectedTenants.length) return;

    if (isSingleTenant && singleTenant) {
      const payload = buildTenantFeaturesPayload(
        singleTenant,
        singleOriginalFeatures,
        singleEditedFeatures
      );
      if (!tenantFeaturesPayloadHasChanges(payload)) return;
      const success = await onApply(payload);
      if (success) {
        setShowReview(false);
        setSingleOriginalFeatures(normalizeTenantFeatures(singleEditedFeatures));
      }
      return;
    }

    if (operation === TENANT_FEATURE_BULK_OPERATIONS.REPLACE) {
      const confirm = await Swal.fire({
        title: 'Apply replace to selected tenants?',
        html: '<p>This action replaces Tenant Features on every selected tenant.</p>',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Apply Features',
        cancelButtonText: 'Cancel',
      });
      if (!confirm.isConfirmed) return;
    }

    if (!tenantFeaturesPayloadHasChanges(plannedPayload)) return;

    const success = await onApply(plannedPayload);
    if (success) {
      setShowReview(false);
      setInputFeatures([]);
    }
  };

  const suggestions = (featureSuggestions || [])
    .filter((feature) => !inputFeatures.some((item) => item.toLowerCase() === feature.toLowerCase()))
    .slice(0, 8);

  const singleSuggestions = (featureSuggestions || [])
    .filter((feature) => !singleEditedFeatures.some((item) => item.toLowerCase() === feature.toLowerCase()))
    .slice(0, 8);

  const renderEditorEmpty = () => (
    <div className="stm-feature-editor-panel stm-feature-editor-panel--empty">
      <p className="stm-feature-editor-panel__placeholder-title">Tenant features</p>
      <p className="stm-muted">Select one or more tenants on the left to configure features.</p>
    </div>
  );

  const renderReview = () => (
    <div className="stm-feature-editor-panel">
      <h3 className="stm-features-section__title">Review</h3>
      <p className="stm-features-review__lead">
        Applying to {selectedTenants.length} tenant{selectedTenants.length === 1 ? '' : 's'}
      </p>
      {!isSingleTenant && (
        <p className="stm-features-review__detail">
          {OPERATION_LABELS[operation].replace(' features', '')}: {inputFeatures.join(', ') || '—'}
        </p>
      )}
      {isSingleTenant && (
        <div className="stm-labels-cell stm-labels-cell--compact">
          {singleEditedFeatures.map((feature) => (
            <span key={feature} className="stm-label-chip">{feature}</span>
          ))}
        </div>
      )}
      {!isSingleTenant && Object.keys(targetSummary).length > 0 && (
        <p className="stm-features-section__meta">{formatTargetSummaryLine(targetSummary)}</p>
      )}
      {!isSingleTenant && operation === TENANT_FEATURE_BULK_OPERATIONS.REPLACE && (
        <div className="stm-features-inline-warn">
          Existing tenant features will be replaced.
        </div>
      )}
      {error && <p className="stm-field-error">{error}</p>}
      <div className="stm-feature-editor-panel__actions">
        <button type="button" className="stm-btn stm-btn--secondary stm-btn--sm" onClick={() => setShowReview(false)} disabled={isSaving}>
          Back
        </button>
        <button type="button" className="stm-btn stm-btn--primary stm-btn--sm" onClick={handleApply} disabled={isSaving}>
          {isSaving ? 'Applying...' : 'Apply Features'}
        </button>
      </div>
    </div>
  );

  const renderBulkEditor = () => (
    <div className="stm-feature-editor-panel">
      <div className="stm-feature-editor-panel__head">
        <h3 className="stm-features-section__title">Bulk update</h3>
        <p className="stm-features-section__meta">
          {selectedTenants.length} tenants selected
          {Object.keys(targetSummary).length > 0 && (
            <> · {formatTargetSummaryLine(targetSummary)}</>
          )}
        </p>
      </div>
      <div className="stm-segmented-control" role="radiogroup" aria-label="Feature operation">
        {Object.entries(OPERATION_LABELS).map(([key]) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={operation === key}
            className={`stm-segmented-control__item ${operation === key ? 'is-active' : ''} ${key === TENANT_FEATURE_BULK_OPERATIONS.REPLACE ? 'is-risky' : ''}`}
            onClick={() => setOperation(key)}
            disabled={isSaving}
          >
            {SEGMENT_LABELS[key]}
          </button>
        ))}
      </div>
      {operation === TENANT_FEATURE_BULK_OPERATIONS.REPLACE && (
        <div className="stm-features-inline-warn">
          Existing tenant features will be replaced on each selected tenant.
        </div>
      )}
      <p className="stm-modal__label stm-modal__label--compact">
        {operation === TENANT_FEATURE_BULK_OPERATIONS.REMOVE ? 'Features to remove' : 'Features'}
      </p>
      <div className="stm-label-editor stm-label-editor--compact">
        {inputFeatures.length ? inputFeatures.map((feature) => (
          <button
            key={feature}
            type="button"
            className="stm-label-editor__chip"
            onClick={() => removeInputFeature(feature)}
            disabled={isSaving}
          >
            {feature}
            <FaTimes aria-hidden />
          </button>
        )) : (
          <span className="stm-muted">Add one or more features</span>
        )}
      </div>
      <div className="stm-label-editor__add">
        <input
          value={newFeature}
          onChange={(event) => setNewFeature(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addInputFeature();
            }
          }}
          placeholder="Add feature"
          disabled={isSaving}
        />
        <button type="button" className="stm-icon-btn" onClick={() => addInputFeature()} disabled={isSaving}>
          <FaPlus />
        </button>
      </div>
      {suggestions.length > 0 && (
        <div className="stm-label-suggestions">
          {suggestions.map((feature) => (
            <button key={feature} type="button" onClick={() => addInputFeature(feature)} disabled={isSaving}>
              {feature}
            </button>
          ))}
        </div>
      )}
      {error && <p className="stm-field-error">{error}</p>}
      <div className="stm-feature-editor-panel__actions">
        <button
          type="button"
          className="stm-btn stm-btn--primary stm-btn--sm"
          disabled={isSaving || !bulkCanProceed}
          onClick={handleOpenReview}
        >
          Review Changes
        </button>
      </div>
    </div>
  );

  const renderSingleEditor = () => (
    <div className="stm-feature-editor-panel">
      <div className="stm-feature-editor-panel__head">
        <h3 className="stm-features-section__title">{getTenantDisplayName(singleTenant)}</h3>
        <p className="stm-features-section__meta">
          {singleTenant.tenantType} · {formatLabelPreview(singleTenant.labels)}
        </p>
      </div>
      <p className="stm-modal__label stm-modal__label--compact">Current tenant features</p>
      <div className="stm-label-editor stm-label-editor--compact">
        {singleEditedFeatures.length ? singleEditedFeatures.map((feature) => (
          <button
            key={feature}
            type="button"
            className="stm-label-editor__chip"
            onClick={() => removeSingleFeature(feature)}
            disabled={isSaving}
          >
            {feature}
            <FaTimes aria-hidden />
          </button>
        )) : (
          <span className="stm-muted">No tenant features</span>
        )}
      </div>
      <div className="stm-label-editor__add">
        <input
          value={newFeature}
          onChange={(event) => setNewFeature(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addSingleFeature();
            }
          }}
          placeholder="Add tenant feature"
          disabled={isSaving}
        />
        <button type="button" className="stm-icon-btn" onClick={() => addSingleFeature()} disabled={isSaving}>
          <FaPlus />
        </button>
      </div>
      {singleSuggestions.length > 0 && (
        <div className="stm-label-suggestions">
          {singleSuggestions.map((feature) => (
            <button key={feature} type="button" onClick={() => addSingleFeature(feature)} disabled={isSaving}>
              {feature}
            </button>
          ))}
        </div>
      )}
      {error && <p className="stm-field-error">{error}</p>}
      <div className="stm-feature-editor-panel__actions">
        <button
          type="button"
          className="stm-btn stm-btn--primary stm-btn--sm"
          disabled={isSaving || !singleHasChanges}
          onClick={handleOpenReview}
        >
          Review &amp; Save
        </button>
      </div>
    </div>
  );

  const renderMain = () => {
    if (showReview) return renderReview();
    if (!selectedTenants.length) return renderEditorEmpty();
    return isSingleTenant ? renderSingleEditor() : renderBulkEditor();
  };

  return (
    <div className="stm-features-workspace">
      <aside className="stm-features-workspace__aside">
        <TenantFeatureTenantSelector
          tenants={tenants}
          selectedTenantIds={selectedTenantIds}
          onSelectedTenantIdsChange={onSelectedTenantIdsChange}
          labelOptions={labelOptions}
        />
      </aside>
      <div className="stm-features-workspace__main">
        {renderMain()}
      </div>
    </div>
  );
};

export default TenantFeaturesPanel;
